import React, { useState } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import firebaseApp from "../../firebase/credenciales";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { initializeApp, getApp, getApps } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuthState } from "react-firebase-hooks/auth";
import * as XLSX from "xlsx";
import addRecordWithId from "../../functions/addRecordWithId";
import { logBusinessAction } from "../../services/AuditService";
import { checkPermission } from "../../functions/checkPermission";
import {
  Container,
  Card,
  Form,
  Button,
  Table,
  Spinner,
  Row,
  Col,
  Badge,
  Alert,
  ProgressBar
} from "react-bootstrap";
import {
  FaUpload,
  FaFileExcel,
  FaDownload,
  FaUsers,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaCloudUploadAlt,
  FaFileImport
} from "react-icons/fa";

// Instancia principal para la sesión del administrador
const auth = getAuth(firebaseApp);

const functions = getFunctions(firebaseApp, "us-central1");

function UsuariosMasivos({ usuario }) {
  const ruc = usuario.ruc;
  const esAdminUsuarios = ["admin", "superAdmin"].includes(String(usuario?.rol || ""));
  const canCargaMasivaUsuarios = checkPermission(usuario, "usuarios", "USUARIOS_MASIVOS_CARGA") || esAdminUsuarios;
  const [user] = useAuthState(auth);
  const [selectedFile, setSelectedFile] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [filterType, setFilterType] = useState("all"); // 'all', 'success', 'error'
  const [validationErrors, setValidationErrors] = useState([]);
  const [isDataValid, setIsDataValid] = useState(false);
  const [tempData, setTempData] = useState([]);

  // Prevenir cierre accidental durante la carga
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (loading) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [loading]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setResults([]);
    setValidationErrors([]);
    setIsDataValid(false);
    setTempData([]);

    if (file) {
      preValidateFile(file);
    }
  };

  const preValidateFile = (file) => {
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "binary" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, range: 1 });

        const errors = [];
        const validRows = [];
        const seenEmails = new Set();
        const seenDNIs = new Set();

        data.forEach((row, index) => {
          if (!row || row.length === 0) return;

          const rowNum = index + 2; // +1 por header, +1 por 1-based
          const [emailRaw, dniRaw, nombresRaw, apellidosRaw] = row;

          const email = clean(emailRaw);
          const dni = normalizeDNI(dniRaw);
          const nombres = clean(nombresRaw);
          const apellidos = clean(apellidosRaw);

          if (!email) errors.push(`Fila ${rowNum}: Email es obligatorio`);
          else if (!validateEmail(email)) errors.push(`Fila ${rowNum}: Email inválido (${email})`);
          else if (seenEmails.has(email)) errors.push(`Fila ${rowNum}: Email duplicado en el archivo (${email})`);

          if (!dni) errors.push(`Fila ${rowNum}: DNI es obligatorio`);
          else if (!validateDNI(dni)) errors.push(`Fila ${rowNum}: DNI inválido (${dni})`);
          else if (seenDNIs.has(dni)) errors.push(`Fila ${rowNum}: DNI duplicado en el archivo (${dni})`);

          if (!nombres) errors.push(`Fila ${rowNum}: Nombres son obligatorios`);
          if (!apellidos) errors.push(`Fila ${rowNum}: Apellidos son obligatorios`);

          seenEmails.add(email);
          seenDNIs.add(dni);
          validRows.push(row);
        });

        setValidationErrors(errors);
        setTempData(data);
        setIsDataValid(errors.length === 0 && data.length > 0);
      } catch (err) {
        setValidationErrors(["Error al leer el archivo Excel"]);
      }
    };
    fileReader.readAsBinaryString(file);
  };

  const exportErrorsToExcel = () => {
    const errorResults = results.filter(r => !r.success);
    if (errorResults.length === 0) return;

    const wsData = [
      ["Email", "DNI", "Nombres", "Apellidos", "Error"],
      ...errorResults.map(r => [r.email, r.dni, r.nombres, r.apellidos, r.status])
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Errores");
    XLSX.writeFile(wb, `errores-carga-${new Date().getTime()}.xlsx`);
  };

  const validateEmail = (email) => {
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailPattern.test(email);
  };

  // Limpieza de cadenas
  const clean = (v) => {
    if (typeof v === "string") {
      return v.trim();
    }
    if (typeof v === "number") {
      return String(v).trim();
    }
    return v ?? "";
  };

  // Normaliza DNI para conservar ceros iniciales cuando Excel lo interpreta como número.
  const normalizeDNI = (value) => {
    const raw = clean(value);
    if (raw === "") return "";

    const digitsOnly = String(raw).replace(/\D/g, "");
    if (!digitsOnly) return "";

    if (digitsOnly.length < 8) {
      return digitsOnly.padStart(8, "0");
    }

    return digitsOnly;
  };

  // Validar DNI (8 o 9 dígitos, solo números)
  const validateDNI = (dni) => {
    const dniStr = normalizeDNI(dni);
    return /^\d{8,9}$/.test(dniStr);
  };

  // Función para generar y descargar la plantilla Excel
  const generarPlantillaExcel = () => {
    // Definir las columnas de la plantilla
    const headers = [
      "Email",
      "DNI",
      "Nombres",
      "Apellidos",
      "Dirección",
      "Legajo",
      "Legajo 2",
      "Celular",
      "Tipo de Contrato",
      "Cargo",
      "Vacaciones",
      "Supervisor",
      "Recursos Humanos"
    ];

    // Datos de ejemplo para mostrar el formato
    const ejemploData = [
      [
        "juan.perez@gmail.com",
        "12345678",
        "Juan Carlos",
        "Pérez González",
        "Av. Principal 123, Lima",
        "EMP001",
        "LEG002",
        "987654321",
        "Tiempo Completo",
        "Analista",
        "15",
        "MariaGarcia@empresa.com",
        "AnaLopez@empresa.com"
      ]
    ];

    // Crear un nuevo workbook
    const workbook = XLSX.utils.book_new();

    // Crear worksheet con headers y datos de ejemplo
    const worksheetData = [headers, ...ejemploData];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Configurar el ancho de las columnas
    const columnWidths = [
      { wch: 25 }, // Email
      { wch: 12 }, // DNI
      { wch: 20 }, // Nombres
      { wch: 20 }, // Apellidos
      { wch: 30 }, // Dirección
      { wch: 12 }, // Legajo
      { wch: 12 }, // Legajo 2
      { wch: 12 }, // Celular
      { wch: 15 }, // Tipo de Contrato
      { wch: 15 }, // Cargo
      { wch: 10 }, // Vacaciones
      { wch: 20 }, // Supervisor
      { wch: 20 }  // Recursos Humanos
    ];
    worksheet['!cols'] = columnWidths;

    // Agregar estilos a los headers (primera fila)
    const headerRange = XLSX.utils.decode_range(worksheet['!ref']);
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet[cellAddress]) continue;

      worksheet[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "1E3A8A" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
    }

    // Agregar comentarios/notas a las celdas importantes
    if (!worksheet['!comments']) worksheet['!comments'] = [];

    // Comentario para Email
    worksheet['!comments'].push({
      ref: 'A1',
      author: 'Sistema',
      t: 'Debe ser un email válido y único. Ejemplo: usuario@empresa.com'
    });

    // Comentario para DNI
    worksheet['!comments'].push({
      ref: 'B1',
      author: 'Sistema',
      t: 'Debe tener 8 o 9 dígitos numéricos. Ejemplo: 12345678'
    });

    // Agregar el worksheet al workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Plantilla Usuarios");

    // Crear una segunda hoja con instrucciones
    const instrucciones = [
      ["INSTRUCCIONES PARA CARGA MASIVA DE USUARIOS"],
      [""],
      ["1. CAMPOS OBLIGATORIOS:"],
      ["   • Email: Debe ser único y válido"],
      ["   • DNI: 8 o 9 dígitos numéricos"],
      ["   • Nombres: Nombre completo del empleado"],
      ["   • Apellidos: Apellidos completos del empleado"],
      [""],
      ["2. CAMPOS OPCIONALES:"],
      ["   • Dirección: Dirección de residencia"],
      ["   • Legajo: Código interno del empleado"],
      ["   • Legajo 2: Código secundario o legajo 2 (opcional)"],
      ["   • Celular: Número de teléfono móvil"],
      ["   • Tipo de Contrato: Tiempo Completo, Medio Tiempo, etc."],
      ["   • Cargo: Puesto de trabajo"],
      ["   • Vacaciones: Días de vacaciones por año"],
      ["   • Supervisor: Nombre del supervisor directo"],
      ["   • Recursos Humanos: Encargado de RRHH"],
      [""],
      ["3. NOTAS IMPORTANTES:"],
      ["   • Elimina las filas de ejemplo antes de subir el archivo"],
      ["   • Cada email debe ser único en todo el sistema"],
      ["   • El DNI será usado como contraseña inicial del usuario"],
      ["   • Los campos vacíos se llenarán con valores por defecto"],
      ["   • El archivo debe estar en formato .xlsx o .xls"],
      [""],
      ["4. PROCESO:"],
      ["   • Completa la información en la hoja 'Plantilla Usuarios'"],
      ["   • Guarda el archivo en tu computadora"],
      ["   • Sube el archivo usando el botón 'Procesar Usuarios'"],
      ["   • Revisa los resultados en la tabla de resultados"]
    ];

    const worksheetInstrucciones = XLSX.utils.aoa_to_sheet(instrucciones);

    // Configurar ancho de columna para instrucciones
    worksheetInstrucciones['!cols'] = [{ wch: 60 }];

    // Estilo para el título
    if (worksheetInstrucciones['A1']) {
      worksheetInstrucciones['A1'].s = {
        font: { bold: true, size: 14, color: { rgb: "1E3A8A" } },
        alignment: { horizontal: "center" }
      };
    }

    XLSX.utils.book_append_sheet(workbook, worksheetInstrucciones, "Instrucciones");

    // Generar el archivo y descargarlo
    const fecha = new Date().toISOString().slice(0, 10);
    const nombreArchivo = `plantilla-usuarios-${fecha}.xlsx`;

    XLSX.writeFile(workbook, nombreArchivo);
  };

  const mapRowToUser = (row, headerRow = []) => {
    const normHeaders = (headerRow || []).map((h) => String(h || "").trim().toLowerCase());
    const hasLegajo2Header = normHeaders.some((h) => h.includes("legajo 2") || h.includes("legajo2"));

    let emailRaw, dniRaw, nombresRaw, apellidosRaw, direccionRaw, legajoRaw, legajo2Raw, celularRaw, tipoContratoRaw, cargoRaw, vacacionesRaw, supervisorRaw, rhRaw;

    if (hasLegajo2Header || (Array.isArray(row) && row.length >= 13)) {
      [
        emailRaw, dniRaw, nombresRaw, apellidosRaw, direccionRaw,
        legajoRaw, legajo2Raw, celularRaw, tipoContratoRaw, cargoRaw,
        vacacionesRaw, supervisorRaw, rhRaw
      ] = row;
    } else {
      [
        emailRaw, dniRaw, nombresRaw, apellidosRaw, direccionRaw,
        legajoRaw, celularRaw, tipoContratoRaw, cargoRaw,
        vacacionesRaw, supervisorRaw, rhRaw
      ] = row;
      legajo2Raw = "";
    }

    return {
      email: clean(emailRaw),
      dni: normalizeDNI(dniRaw),
      nombres: clean(nombresRaw),
      apellidos: clean(apellidosRaw),
      direccion: clean(direccionRaw),
      legajo: clean(legajoRaw),
      legajo2: clean(legajo2Raw),
      phone: clean(celularRaw),
      contrato: clean(tipoContratoRaw),
      cargo: clean(cargoRaw),
      vacaciones: clean(vacacionesRaw),
      supervisor: clean(supervisorRaw),
      recursosHumanos: clean(rhRaw),
    };
  };

  const handleUpload = async () => {
    if (!canCargaMasivaUsuarios) {
      setStatusMessage("No tiene permisos para carga masiva de usuarios.");
      return;
    }
    if (!selectedFile || !isDataValid) return;
    setLoading(true);
    setIsButtonDisabled(true);
    setStatusMessage("Preparando datos para carga masiva...");

    const data = tempData;
    const headerRow = data.length > 0 ? data[0] : [];
    const usersToProcess = [];

    // 1. Preparar el array de usuarios para enviar al servidor
    for (const row of data) {
      if (row && row.length > 0) {
        const u = mapRowToUser(row, headerRow);

        if (u.email && validateEmail(u.email) && u.dni && validateDNI(u.dni)) {
          usersToProcess.push({
            ...u,
            rucEmpresa: ruc || "",
            // Señal explícita para persistir UID de Firebase Auth en Firestore.
            includeFirebaseUid: true,
          });
        }
      }
    }

    if (usersToProcess.length === 0) {
      setStatusMessage("No se encontraron usuarios válidos para procesar.");
      setLoading(false);
      setIsButtonDisabled(false);
      return;
    }

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Sesión no válida. Vuelve a iniciar sesión e intenta nuevamente.");
      }

      // Fuerza renovación del token para que la callable reciba context.auth
      const idToken = await currentUser.getIdToken(true);

      setStatusMessage(`Enviando ${usersToProcess.length} usuarios al servidor...`);
      setProgress(20);

      // 2. Llamar a la Cloud Function
      const bulkCreateUsers = httpsCallable(functions, "bulkCreateUsers");
      const response = await bulkCreateUsers({
        users: usersToProcess,
        baseDatos: "users",
        autor: currentUser.email || user?.email || "",
        rucEmpresa: ruc,
        includeFirebaseUid: true,
        idToken
      });

      if (response.data && response.data.results) {
        setResults(response.data.results);
        setStatusMessage("Carga masiva completada con éxito.");
        setProgress(100);

        // Registrar auditoría
        const exitosos = response.data.results.filter(r => r.success).length;
        if (exitosos > 0) {
          await logBusinessAction(
            "CARGA_MASIVA_USUARIOS",
            "GestionUsuarios",
            { cantidad: exitosos },
            usuario
          );
        }
      } else {
        throw new Error("Respuesta inesperada del servidor");
      }
    } catch (error) {
      console.error("Error en carga masiva:", error);
      setStatusMessage(`Error crítico: ${error.message}`);
      
      // En caso de error, mostramos lo que pudimos recolectar
      setResults(usersToProcess.map(u => ({
        ...u,
        success: false,
        status: `Error de conexión: ${error.message}`
      })));
    }

    setLoading(false);
    setIsButtonDisabled(false);
    // Limpiamos el progreso después de un momento
    setTimeout(() => setProgress(0), 3000);
  };

  const calcularEstadisticas = () => {
    const exitosos = results.filter((r) => r.success).length;
    const errores = results.filter((r) => !r.success).length;
    const total = results.length;
    const porcentajeExito =
      total > 0 ? Math.round((exitosos / total) * 100) : 0;

    return {
      total,
      exitosos,
      errores,
      porcentajeExito,
    };
  };

  const estadisticas = calcularEstadisticas();

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium con degradado azul para Carga Masiva */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
              borderBottom: "3px solid #f59e0b",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaFileImport className="me-3" />
                Carga Masiva de Usuarios
              </h2>
              <p className="mb-0 opacity-75">
                Importa múltiples usuarios desde un archivo Excel
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Indicador de carga para operaciones */}
            {loading && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
                style={{ zIndex: 1080 }}
              >
                <div className="bg-white p-4 rounded-3 shadow-lg" style={{ minWidth: "300px", maxWidth: "400px" }}>
                  <div className="text-center mb-3">
                    <Spinner
                      animation="border"
                      variant="primary"
                      size="sm"
                      className="me-2"
                    />
                    <span className="fw-bold text-primary">Procesando...</span>
                  </div>

                  <ProgressBar
                    now={progress}
                    label={`${progress}%`}
                    variant="primary"
                    animated
                    className="mb-2"
                    style={{ height: "20px" }}
                  />

                  <p className="text-center text-muted small mb-0">
                    {statusMessage}
                  </p>
                  <p className="text-center text-muted x-small mt-1" style={{ fontSize: "0.75rem" }}>
                    Por favor no cierres esta ventana
                  </p>
                </div>
              </div>
            )}

            {user ? (
              <>
                {!canCargaMasivaUsuarios && (
                  <Alert variant="warning" className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
                    No tiene permisos para realizar carga masiva de usuarios.
                  </Alert>
                )}
                {/* Barra superior con información */}
                <Card
                  className="shadow-sm border-0 mb-4"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body className="p-4">
                    <Row className="align-items-center">
                      <Col md={1}>
                        <div
                          className="rounded-3 p-3 text-center"
                          style={{
                            background:
                              "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                            color: "white",
                          }}
                        >
                          <FaFileImport size={24} />
                        </div>
                      </Col>
                      <Col md={11}>
                        <h5 className="mb-1 fw-semibold">
                          Importación Masiva de Usuarios
                        </h5>
                        <p className="text-muted mb-0 small">
                          Carga múltiples usuarios de forma rápida y eficiente desde archivos Excel
                        </p>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Estadísticas de resultados */}
                {results.length > 0 && (
                  <Row className="mb-4">
                    <Col md={3}>
                      <Card
                        className="border-0 shadow-sm"
                        style={{ borderRadius: "15px" }}
                      >
                        <Card.Body
                          className="text-center"
                          style={{
                            background:
                              "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                            color: "white",
                            borderRadius: "15px",
                          }}
                        >
                          <h4 className="fw-bold">{estadisticas.total}</h4>
                          <p className="mb-0">Total Procesados</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card
                        className="border-0 shadow-sm"
                        style={{ borderRadius: "15px" }}
                      >
                        <Card.Body
                          className="text-center"
                          style={{
                            background:
                              "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                            color: "white",
                            borderRadius: "15px",
                          }}
                        >
                          <h4 className="fw-bold">{estadisticas.exitosos}</h4>
                          <p className="mb-0">Exitosos</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card
                        className="border-0 shadow-sm"
                        style={{ borderRadius: "15px" }}
                      >
                        <Card.Body
                          className="text-center"
                          style={{
                            background:
                              "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                            color: "white",
                            borderRadius: "15px",
                          }}
                        >
                          <h4 className="fw-bold">{estadisticas.errores}</h4>
                          <p className="mb-0">Con Errores</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card
                        className="border-0 shadow-sm"
                        style={{ borderRadius: "15px" }}
                      >
                        <Card.Body
                          className="text-center"
                          style={{
                            background:
                              "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                            color: "white",
                            borderRadius: "15px",
                          }}
                        >
                          <h4 className="fw-bold">{estadisticas.porcentajeExito}%</h4>
                          <p className="mb-0">Tasa de Éxito</p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}

                {/* Alerta informativa */}
                <Alert
                  variant="info"
                  className="border-0 shadow-sm mb-4"
                  style={{ borderRadius: "15px" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <FaExclamationTriangle className="text-info" />
                    <div>
                      <strong>Información importante:</strong>
                      <br />
                      <small>
                        Genera la plantilla Excel, complétala con los datos de los usuarios y súbela para procesarla.
                        Asegúrate de que todos los emails sean únicos y válidos.
                      </small>
                    </div>
                  </div>
                </Alert>

                <Row className="g-4">
                  {/* Sección de descarga de plantilla */}
                  <Col md={6}>
                    <Card
                      className="border-0 shadow-sm h-100"
                      style={{ borderRadius: "15px" }}
                    >
                      <Card.Header
                        className="text-white py-3"
                        style={{
                          background:
                            "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                          borderRadius: "15px 15px 0 0",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <FaFileExcel className="me-2" />
                          <div>
                            <h5 className="mb-0 fw-semibold">Plantilla Excel</h5>
                            <small className="opacity-75">
                              Genera el formato requerido
                            </small>
                          </div>
                        </div>
                      </Card.Header>
                      <Card.Body className="p-4">
                        <p className="text-muted mb-3">
                          Genera la plantilla personalizada con ejemplos y complétala con la información de los usuarios siguiendo el formato establecido.
                        </p>
                        <div className="mb-3">
                          <h6 className="fw-semibold mb-2">Campos Obligatorios:</h6>
                          <ul className="small text-muted mb-0">
                            <li>• Email: Debe ser único y válido</li>
                            <li>• DNI: 8 o 9 dígitos numéricos</li>
                            <li>• Nombres: Nombre completo del empleado</li>
                            <li>• Apellidos: Apellidos completos del empleado</li>
                          </ul>
                        </div>
                        <Button
                          variant="success"
                          onClick={generarPlantillaExcel}
                          className="w-100 d-flex align-items-center justify-content-center gap-2 py-3"
                          style={{
                            borderRadius: "10px",
                            boxShadow: "0 4px 8px rgba(34, 197, 94, 0.3)",
                          }}
                        >
                          <FaDownload />
                          Generar Plantilla Excel
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Sección de carga de archivo */}
                  <Col md={6}>
                    <Card
                      className="border-0 shadow-sm h-100"
                      style={{ borderRadius: "15px" }}
                    >
                      <Card.Header
                        className="text-white py-3"
                        style={{
                          background:
                            "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                          borderRadius: "15px 15px 0 0",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <FaCloudUploadAlt className="me-2" />
                          <div>
                            <h5 className="mb-0 fw-semibold">Subir Archivo</h5>
                            <small className="opacity-75">
                              Selecciona tu archivo Excel
                            </small>
                          </div>
                        </div>
                      </Card.Header>
                      <Card.Body className="p-4">
                        <Form.Group controlId="formFile" className="mb-3">
                          <Form.Label className="fw-semibold">
                            Archivo Excel (.xlsx, .xls)
                          </Form.Label>
                          <Form.Control
                            type="file"
                            onChange={handleFileChange}
                            className="form-control-lg"
                            accept=".xlsx,.xls"
                            disabled={!canCargaMasivaUsuarios}
                            style={{
                              borderRadius: "10px",
                              border: "2px dashed #dee2e6",
                              padding: "20px",
                            }}
                          />
                          <Form.Text className="text-muted">
                            {selectedFile
                              ? `Archivo seleccionado: ${selectedFile.name}`
                              : "Selecciona el archivo Excel con los datos de los usuarios"
                            }
                          </Form.Text>
                        </Form.Group>

                        {selectedFile && (
                          <div className="bg-light p-3 rounded-3 mb-3">
                            <div className="d-flex align-items-center gap-2">
                              <FaFileExcel className="text-success" />
                              <div>
                                <small className="fw-semibold d-block">
                                  {selectedFile.name}
                                </small>
                                <small className="text-muted">
                                  {(selectedFile.size / 1024).toFixed(1)} KB
                                </small>
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedFile && validationErrors.length > 0 && (
                          <Alert variant="warning" className="small py-2 mb-3">
                            <div className="fw-bold mb-1">Se encontraron errores en el archivo:</div>
                            <ul className="mb-0 ps-3">
                              {validationErrors.slice(0, 5).map((err, i) => (
                                <li key={i}>{err}</li>
                              ))}
                              {validationErrors.length > 5 && <li>Y {validationErrors.length - 5} errores más...</li>}
                            </ul>
                          </Alert>
                        )}

                        <Button
                          variant="primary"
                          onClick={handleUpload}
                          disabled={!selectedFile || loading || isButtonDisabled || !isDataValid || !canCargaMasivaUsuarios}
                          className="w-100 d-flex align-items-center justify-content-center gap-2 py-3"
                          style={{
                            borderRadius: "10px",
                            background: (selectedFile && isDataValid)
                              ? "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)"
                              : "linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)",
                            border: "none",
                            boxShadow: (selectedFile && isDataValid)
                              ? "0 4px 8px rgba(59, 130, 246, 0.3)"
                              : "none",
                          }}
                        >
                          <FaUpload />
                          {loading ? "Procesando..." : "Procesar Usuarios"}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Tabla de resultados */}
                {results.length > 0 && (
                  <Card
                    className="border-0 shadow-sm mt-4"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Header
                      className="text-white py-3"
                      style={{
                        background:
                          "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                        borderRadius: "15px 15px 0 0",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <FaUsers className="me-2" />
                          <div>
                            <h5 className="mb-0 fw-semibold">
                              Resultados de la Carga
                            </h5>
                            <small className="opacity-75">
                              Estado del procesamiento de usuarios
                            </small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          {results.some(r => !r.success) && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="rounded-pill px-3"
                              onClick={exportErrorsToExcel}
                            >
                              <FaDownload className="me-1" /> Descargar Errores
                            </Button>
                          )}
                          <div className="btn-group btn-group-sm bg-white rounded-pill p-1 shadow-sm">
                            <Button
                              variant={filterType === "all" ? "primary" : "outline-primary"}
                              className="rounded-pill border-0 px-3"
                              onClick={() => setFilterType("all")}
                            >
                              Todos
                            </Button>
                            <Button
                              variant={filterType === "success" ? "success" : "outline-success"}
                              className="rounded-pill border-0 px-3"
                              onClick={() => setFilterType("success")}
                            >
                              Éxito
                            </Button>
                            <Button
                              variant={filterType === "error" ? "danger" : "outline-danger"}
                              className="rounded-pill border-0 px-3"
                              onClick={() => setFilterType("error")}
                            >
                              Errores
                            </Button>
                          </div>
                          <Badge
                            bg="light"
                            text="dark"
                            style={{ fontSize: "0.9rem" }}
                          >
                            {results.length} registros
                          </Badge>
                          <Badge
                            bg={estadisticas.porcentajeExito >= 80 ? "success" : "warning"}
                            style={{ fontSize: "0.8rem" }}
                          >
                            {estadisticas.porcentajeExito}% éxito
                          </Badge>
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                        <Table
                          responsive
                          hover
                          className="mb-0"
                          style={{ fontSize: "0.9rem" }}
                        >
                          <thead
                            style={{
                              backgroundColor: "#f8f9fa",
                              position: "sticky",
                              top: 0,
                              zIndex: 10,
                            }}
                          >
                            <tr>
                              <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                                #
                              </th>
                              <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                DNI
                              </th>
                              <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                Nombres
                              </th>
                              <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                Apellidos
                              </th>
                              <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                Email
                              </th>
                              <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                                Estado
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {results
                              .filter(res => {
                                if (filterType === "success") return res.success;
                                if (filterType === "error") return !res.success;
                                return true;
                              })
                              .map((res, index) => (
                                <tr
                                  key={index}
                                  style={{ borderBottom: "1px solid #f1f3f4" }}
                                >
                                  <td className="text-center py-3 px-3">
                                    <Badge
                                      bg="dark"
                                      className="px-2 py-1"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {index + 1}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-3">
                                    <Badge
                                      bg="secondary"
                                      className="px-2 py-1"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {res.dni}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-3">
                                    <div className="fw-medium text-dark">
                                      {res.nombres}
                                    </div>
                                  </td>
                                  <td className="py-3 px-3 fw-medium text-dark">
                                    {res.apellidos}
                                  </td>
                                  <td className="py-3 px-3 text-muted small">
                                    {res.email}
                                  </td>
                                  <td className="text-center py-3 px-3">
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      {res.success ? (
                                        <FaCheckCircle className="text-success" size="16px" />
                                      ) : (
                                        <FaTimesCircle className="text-danger" size="16px" />
                                      )}
                                      <span
                                        className={`small ${res.success ? "text-success" : "text-danger"
                                          }`}
                                      >
                                        {res.status}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </>
            ) : (
              <Card
                className="border-0 shadow-sm"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="text-center py-5">
                  <div
                    className="bg-light rounded-circle mx-auto mb-3"
                    style={{
                      width: "80px",
                      height: "80px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaUsers size={32} className="text-muted" />
                  </div>
                  <h5 className="text-muted mb-2">Acceso Requerido</h5>
                  <p className="text-muted mb-0">
                    Inicia sesión para acceder a la funcionalidad de carga masiva
                  </p>
                </Card.Body>
              </Card>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default UsuariosMasivos;
